import { JiraBoard } from './listings';

const ID_SEPARATOR = ',';
const NUMERIC_ID = /^[0-9]+$/;

export const FILTER_ID_LIST = /^([0-9]+(,[0-9]+)*)?$/;

export type BoardSelection = {
  boardIds: number[];
  filterIds: string[];
};

export const NO_SELECTION: BoardSelection = { boardIds: [], filterIds: [] };

export const asSelection = (
  value?: Partial<BoardSelection> | null,
): BoardSelection => ({
  boardIds: value?.boardIds ?? [],
  filterIds: value?.filterIds ?? [],
});

export const parseIds = (raw?: string): string[] =>
  (raw ?? '')
    .split(ID_SEPARATOR)
    .map(token => token.trim())
    .filter(token => NUMERIC_ID.test(token));

export const parseBoardIds = (raw?: string): number[] =>
  parseIds(raw).map(Number);

export const formatIds = (ids: readonly (string | number)[]): string =>
  ids.join(ID_SEPARATOR);

export const isSelectable = (board: JiraBoard): boolean =>
  Boolean(board.filter);

export const boardLabel = (board: JiraBoard): string =>
  `${board.name} (#${board.id})`;

const dedupe = (ids: readonly string[]): string[] => [...new Set(ids)];

export const selectionOf = (
  boards: readonly JiraBoard[],
  boardIds: readonly number[],
): BoardSelection => {
  const wanted = new Set(boardIds);
  const selected = boards.filter(
    board => wanted.has(board.id) && isSelectable(board),
  );
  return {
    boardIds: selected.map(board => board.id),
    filterIds: dedupe(
      selected.flatMap(board => (board.filter ? [board.filter.id] : [])),
    ),
  };
};

export const toggled = (
  boards: readonly JiraBoard[],
  selection: BoardSelection,
  boardId: number,
): BoardSelection =>
  selectionOf(
    boards,
    selection.boardIds.includes(boardId)
      ? selection.boardIds.filter(id => id !== boardId)
      : [...selection.boardIds, boardId],
  );

export const soleBoardSelection = (
  boards: readonly JiraBoard[],
): BoardSelection => {
  const selectable = boards.filter(isSelectable);
  return selectable.length === 1
    ? selectionOf(boards, [selectable[0].id])
    : NO_SELECTION;
};

export const prunedSelection = (
  boards: readonly JiraBoard[],
  selection: BoardSelection,
): BoardSelection => {
  if (selection.boardIds.length === 0) return selection;
  const matched = selectionOf(boards, selection.boardIds);
  const listed = new Set(boards.map(board => board.id));
  // A prune never widens the scope on its own: with nothing matched, the stored filter ids stand.
  if (!selection.boardIds.every(id => listed.has(id))) {
    return matched.filterIds.length > 0
      ? matched
      : { boardIds: [], filterIds: selection.filterIds };
  }
  return {
    boardIds: matched.boardIds,
    filterIds: dedupe([...matched.filterIds, ...selection.filterIds]),
  };
};

export const sameSelection = (a: BoardSelection, b: BoardSelection): boolean =>
  formatIds(a.boardIds) === formatIds(b.boardIds) &&
  formatIds(a.filterIds) === formatIds(b.filterIds);

export const equivalentBoardNames = (
  board: JiraBoard,
  boards: readonly JiraBoard[],
): string[] => {
  const equivalent = new Set(board.filter?.equivalentBoardIds ?? []);
  return boards.filter(other => equivalent.has(other.id)).map(boardLabel);
};
